import React, { useState } from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../api';

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | already | error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      const res = await api.subscribe(email.trim());
      if (res.status === 'already_subscribed') {
        setStatus('already');
        setMessage("You're already on the list. Thanks for sticking around.");
      } else {
        setStatus('success');
        setMessage("You're in. Look for our next dispatch in your inbox.");
      }
      setEmail('');
    } catch (err) {
      setStatus('error');
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      setMessage(msg || 'Could not subscribe. Please try again.');
    }
  };

  const isDone = status === 'success' || status === 'already';

  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6 }}
      data-testid="newsletter-signup"
      className="bg-[#242326] rounded-2xl p-8 md:p-12 lg:p-16 my-16 md:my-20 overflow-hidden relative"
    >
      <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#83BF42]/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-2xl">
        <span className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-bold font-['Inter']">
          Newsletter
        </span>
        <h3 className="text-white text-2xl md:text-4xl font-light leading-tight tracking-tight font-['Inter'] mt-4 mb-3">
          Get our thinking first.
        </h3>
        <p className="text-white/60 text-base md:text-lg font-['Lato'] font-light mb-8 leading-relaxed">
          One opinionated email a month on growth marketing, adtech, and what's actually working in African markets. No fluff.
        </p>

        {isDone ? (
          <div className="flex items-center gap-3 text-[#83BF42] font-['Inter']" data-testid="newsletter-success">
            <CheckCircle2 className="w-5 h-5" />
            <span>{message}</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="newsletter-email-input"
              className="flex-1 px-5 py-3.5 rounded-full bg-white/5 border border-white/10 text-white placeholder-white/30 font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              data-testid="newsletter-submit"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#83BF42] text-[#242326] text-sm font-medium font-['Inter'] hover:bg-[#A8D96C] transition-all duration-300 group disabled:opacity-50 whitespace-nowrap"
            >
              {status === 'loading' ? 'Subscribing…' : 'Subscribe'}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </form>
        )}

        {status === 'error' && (
          <p className="text-red-400 text-xs font-['Lato'] mt-3" data-testid="newsletter-error">
            {message}
          </p>
        )}
      </div>
    </motion.section>
  );
};

export default NewsletterSignup;
